import { applyFieldKind, CATALOG, catalogLabel, measureLabel } from "./catalog";
import { buildProjectFile, fileNameFromTitle, parseProjectFile } from "./project-file";
import { createShareLink, type ShareHours } from "./share";
import { getRoomWallHeight, setAllRoomWallHeights, setRoomWallHeight, WALL_FACE_LABEL, type WallFace } from "./room";
import { clampAperture, clampDoor, ensureApertures, wallApertures } from "./wall";
import { EditorScene } from "./scene";
import { store } from "./state";
import {
  defaultProject,
  isAddKind,
  type FieldKind,
  type Item,
  type ItemKind,
  type RoofKind,
  type WallKind,
} from "./types";

export function bindUI(editor: EditorScene): void {
  const $ = <T extends HTMLElement>(id: string) => {
    const el = document.getElementById(id);
    if (!el) throw new Error(`#${id}`);
    return el as T;
  };

  const catalogBox = $("catalog-btns");
  catalogBox.innerHTML = "";
  for (const entry of CATALOG) {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = entry.label;
    b.dataset.add = entry.kind;
    catalogBox.appendChild(b);
  }

  const lotW = $<HTMLInputElement>("lot-w");
  const lotD = $<HTMLInputElement>("lot-d");
  const interiors = $<HTMLInputElement>("show-interiors");
  const showNames = $<HTMLInputElement>("show-names");
  const showMeasures = $<HTMLInputElement>("show-measures");
  const itemName = $<HTMLInputElement>("item-name");
  const itemW = $<HTMLInputElement>("item-w");
  const itemD = $<HTMLInputElement>("item-d");
  const itemH = $<HTMLInputElement>("item-h");
  const itemRot = $<HTMLInputElement>("item-rot");
  const itemRotDeg = $<HTMLInputElement>("item-rot-deg");
  const hour = $<HTMLInputElement>("hour");
  const hourLabel = $("hour-label");
  const lotLabel = $("lot-label");
  const measure = $("measure");
  const fileInput = $<HTMLInputElement>("file-input");
  const secItem = $("sec-item");
  const secHouse = $("sec-house");
  const secRoom = $("sec-room");
  const secField = $("sec-field");
  const secCanopy = $("sec-canopy");
  const wrapItemH = $("wrap-item-h");
  const itemHCaption = $("item-h-caption");
  const wrapItemDims = $("wrap-item-dims");
  const secWall = $("sec-wall");
  const wallLen = $<HTMLInputElement>("wall-len");
  const wallTh = $<HTMLInputElement>("wall-th");
  const wallH = $<HTMLInputElement>("wall-h");
  const wallDoorFields = $("wall-door-fields");
  const apertureLabel = $("aperture-label");
  const doorW = $<HTMLInputElement>("door-w");
  const doorH = $<HTMLInputElement>("door-h");
  const doorOff = $<HTMLInputElement>("door-off");
  const doorSill = $<HTMLInputElement>("door-sill");
  const canopyH0 = $<HTMLInputElement>("canopy-h0");
  const canopyH1 = $<HTMLInputElement>("canopy-h1");
  const projectSelect = $<HTMLSelectElement>("project-select");
  const projectTitle = $<HTMLInputElement>("project-title");
  const saveState = $("save-state");
  const houseOpeningList = $("house-opening-list");
  const roomOpeningList = $("room-opening-list");
  const btnDelHouseOpening = $("btn-del-house-opening");
  const btnDelRoomOpening = $("btn-del-room-opening");

  const sync = () => {
    const p = store.project;
    const item = store.selected();
    lotW.value = String(p.lot.width);
    lotD.value = String(p.lot.depth);
    interiors.checked = p.showInteriors;
    showNames.checked = p.showNames;
    showMeasures.checked = p.showMeasures;
    hour.value = String(p.hour);
    hourLabel.textContent = formatHour(p.hour);
    lotLabel.textContent = `Lote ${p.lot.width} × ${p.lot.depth} m`;
    if (document.activeElement !== projectTitle) projectTitle.value = store.title;
    if (document.activeElement !== projectSelect) {
      projectSelect.innerHTML = "";
      for (const row of store.library) {
        const opt = document.createElement("option");
        opt.value = row.id;
        opt.textContent = row.title;
        projectSelect.appendChild(opt);
      }
      if (store.cloudId) projectSelect.value = store.cloudId;
    }
    saveState.textContent = store.saving ? "Guardando…" : store.cloudError ? "Error al guardar" : store.uid ? "En la nube" : "";
    saveState.title = store.cloudError ?? "";

    secItem.hidden = !item;
    secHouse.hidden = item?.kind !== "house";
    secRoom.hidden = item?.kind !== "room";
    secField.hidden = item?.kind !== "field";
    secCanopy.hidden = item?.kind !== "canopy";
    secWall.hidden = item?.kind !== "wall";
    wrapItemH.hidden = item?.kind === "canopy";
    wrapItemDims.hidden = item?.kind === "wall";

    if (item) {
      itemName.value = item.name;
      itemW.value = String(item.width);
      itemD.value = String(item.depth);
      if (item.kind === "room" && store.wallFace !== null) {
        itemH.value = String(getRoomWallHeight(item, store.wallFace));
        itemHCaption.textContent = `Alto ${WALL_FACE_LABEL[store.wallFace]}`;
      } else {
        itemH.value = String(item.height);
        itemHCaption.textContent = "Alto";
      }
      const deg = degFromRad(item.rotation);
      itemRot.value = String(Math.round(deg));
      if (document.activeElement !== itemRotDeg) itemRotDeg.value = String(deg);
      if (item.kind === "canopy") {
        canopyH0.value = String(item.height);
        canopyH1.value = String(item.heightEnd ?? item.height);
      }
      fillOpeningList(houseOpeningList, item?.kind === "house" ? item : null);
      fillOpeningList(roomOpeningList, item?.kind === "room" ? item : null);
      const hasOpening = Boolean(item?.openings?.length);
      const openingOn = Boolean(item?.openings?.some((op) => op.id === store.apertureId));
      btnDelHouseOpening.hidden = item?.kind !== "house" || !hasOpening;
      btnDelRoomOpening.hidden = item?.kind !== "room" || !hasOpening;
      btnDelHouseOpening.toggleAttribute("disabled", item?.kind === "house" && !openingOn);
      btnDelRoomOpening.toggleAttribute("disabled", item?.kind === "room" && !openingOn);
      if (item.kind === "wall") {
        wallLen.value = String(item.width);
        wallTh.value = String(item.depth);
        wallH.value = String(item.height);
        const aps = wallApertures(item);
        const ap = aps.find((a) => a.id === store.apertureId) ?? aps[0] ?? null;
        wallDoorFields.hidden = !ap;
        if (ap) {
          apertureLabel.textContent = ap.kind === "door" ? "Puerta" : "Ventana";
          doorW.value = String(ap.width);
          doorH.value = String(ap.height);
          doorOff.value = String(ap.offset);
          doorSill.value = String(ap.sill);
        }
      }
      const pair = store.selectedItems();
      if (pair.length === 2) {
        const [a, b] = pair;
        const dh = Math.abs(a.height - b.height);
        measure.textContent = `${a.name} alto ${a.height.toFixed(2)} m  ·  ${b.name} alto ${b.height.toFixed(2)} m  ·  Δ ${dh.toFixed(2)} m`;
      } else {
        measure.textContent = `${measureLabel(item)} · ${deg}°`;
      }
    } else {
      measure.textContent = "Nada seleccionado";
      fillOpeningList(houseOpeningList, null);
      fillOpeningList(roomOpeningList, null);
      btnDelHouseOpening.hidden = true;
      btnDelRoomOpening.hidden = true;
    }

    document.querySelectorAll("[data-add]").forEach((b) => {
      b.classList.toggle("active", store.placeMode === b.getAttribute("data-add"));
    });
    document.querySelectorAll("[data-roof]").forEach((b) => {
      b.classList.toggle("active", item?.kind === "house" && b.getAttribute("data-roof") === item.roof);
    });
    document.querySelectorAll("[data-wall]").forEach((b) => {
      b.classList.toggle("active", item?.kind === "house" && b.getAttribute("data-wall") === item.wall);
    });
    document.querySelectorAll("[data-place]").forEach((b) => {
      b.classList.toggle("active", store.placeMode === b.getAttribute("data-place"));
    });
    document.querySelectorAll("[data-field]").forEach((b) => {
      b.classList.toggle("active", item?.kind === "field" && b.getAttribute("data-field") === item.fieldKind);
    });
    document.querySelectorAll("[data-room-name]").forEach((b) => {
      b.classList.toggle("active", item?.kind === "room" && item.name.startsWith(b.getAttribute("data-room-name") ?? ""));
    });
    document.querySelectorAll("[data-room-face]").forEach((b) => {
      b.classList.toggle("active", item?.kind === "room" && String(store.wallFace) === b.getAttribute("data-room-face"));
    });
    document.querySelectorAll("[data-cover]").forEach((b) => {
      b.classList.toggle("active", item?.kind === "canopy" && b.getAttribute("data-cover") === (item.cover ?? "chapa"));
    });

    const badge = $("view-badge");
    $("btn-exit-view").hidden = !store.viewOnly;
    if (store.viewOnly && store.shareExpiresAt) {
      badge.hidden = false;
      badge.textContent = `${store.title} · vence ${new Date(store.shareExpiresAt).toLocaleString("es-AR", {
        dateStyle: "short",
        timeStyle: "short",
      })}`;
    } else {
      badge.hidden = true;
    }

    const compassN = $("compass-n");
    const north = store.project.north ?? 0;
    const radius = 16;
    compassN.style.left = `${26 + Math.sin(north) * radius}px`;
    compassN.style.top = `${26 - Math.cos(north) * radius}px`;
    compassN.style.transform = "translate(-50%, -50%)";

    $("hint").textContent = hintText();
  };

  store.subscribe(sync);
  sync();

  const rebuild = () => {
    editor.rebuild();
    store.notify();
  };

  lotW.addEventListener("change", () => {
    store.project.lot.width = num(lotW.value, 20);
    rebuild();
  });
  lotD.addEventListener("change", () => {
    store.project.lot.depth = num(lotD.value, 20);
    rebuild();
  });
  interiors.addEventListener("change", () => {
    store.project.showInteriors = interiors.checked;
    rebuild();
  });
  showNames.addEventListener("change", () => {
    store.project.showNames = showNames.checked;
    editor.applyOverlays();
    store.notify();
  });
  showMeasures.addEventListener("change", () => {
    store.project.showMeasures = showMeasures.checked;
    editor.applyOverlays();
    store.notify();
  });
  hour.addEventListener("input", () => {
    store.project.hour = num(hour.value, 10);
    editor.updateSun();
    store.notify();
  });

  const compass = $("compass");
  const northFromPointer = (ev: PointerEvent) => {
    const r = compass.getBoundingClientRect();
    return Math.atan2(ev.clientX - (r.left + r.width / 2), -(ev.clientY - (r.top + r.height / 2)));
  };
  let draggingNorth = false;
  compass.addEventListener("pointerdown", (ev) => {
    ev.preventDefault();
    draggingNorth = true;
    compass.setPointerCapture(ev.pointerId);
    store.beginEdit();
    store.project.north = northFromPointer(ev);
    editor.updateNorth();
    store.notify();
  });
  compass.addEventListener("pointermove", (ev) => {
    if (!draggingNorth) return;
    store.project.north = northFromPointer(ev);
    editor.updateNorth();
    store.notify();
  });
  const endNorth = () => {
    if (!draggingNorth) return;
    draggingNorth = false;
    store.endEdit();
  };
  compass.addEventListener("pointerup", endNorth);
  compass.addEventListener("pointercancel", endNorth);

  itemName.addEventListener("change", () => {
    const item = store.selected();
    if (!item) return;
    store.checkpoint();
    item.name = itemName.value.trim() || catalogLabel(item.kind);
    rebuild();
  });
  itemW.addEventListener("change", () => {
    const item = store.selected();
    if (!item) return;
    store.checkpoint();
    item.width = num(itemW.value, 0.3);
    if (item.kind === "field") item.fieldKind = "custom";
    rebuild();
  });
  itemD.addEventListener("change", () => {
    const item = store.selected();
    if (!item) return;
    store.checkpoint();
    item.depth = num(itemD.value, 0.3);
    if (item.kind === "field") item.fieldKind = "custom";
    rebuild();
  });
  itemH.addEventListener("change", () => {
    const item = store.selected();
    if (!item) return;
    store.checkpoint();
    const h = num(itemH.value, 0.1);
    if (item.kind === "room" && store.wallFace !== null) setRoomWallHeight(item, store.wallFace, h);
    else if (item.kind === "room") setAllRoomWallHeights(item, h);
    else item.height = h;
    rebuild();
  });
  const applyRotation = (raw: string) => {
    const item = store.selected();
    if (!item) return;
    item.rotation = (wrapDeg(raw) * Math.PI) / 180;
    editor.syncTransforms();
    store.notify();
  };
  itemRot.addEventListener("pointerdown", () => store.beginEdit());
  itemRot.addEventListener("pointerup", () => store.endEdit());
  itemRot.addEventListener("input", () => applyRotation(itemRot.value));
  itemRotDeg.addEventListener("input", () => applyRotation(itemRotDeg.value));
  itemRotDeg.addEventListener("change", () => {
    itemRotDeg.value = String(wrapDeg(itemRotDeg.value));
  });

  $("rot-left").addEventListener("click", () => {
    if (!store.selectedId) return;
    store.rotateItem(store.selectedId, -Math.PI / 2);
    editor.syncTransforms();
    store.notify();
  });
  $("rot-right").addEventListener("click", () => {
    if (!store.selectedId) return;
    store.rotateItem(store.selectedId, Math.PI / 2);
    editor.syncTransforms();
    store.notify();
  });
  $("btn-duplicate").addEventListener("click", () => {
    store.duplicateSelected();
    rebuild();
  });
  $("btn-delete").addEventListener("click", () => {
    store.removeSelected();
    rebuild();
  });

  catalogBox.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest("[data-add]");
    if (!(btn instanceof HTMLElement)) return;
    const kind = btn.getAttribute("data-add") as ItemKind;
    store.setPlace(kind);
    if (kind !== "wall") editor.setWallPreview(null, null);
    store.notify();
  });

  document.querySelectorAll("[data-roof]").forEach((b) => {
    b.addEventListener("click", () => {
      const item = store.selected();
      if (item?.kind !== "house") return;
      item.roof = b.getAttribute("data-roof") as RoofKind;
      rebuild();
    });
  });
  document.querySelectorAll("[data-wall]").forEach((b) => {
    b.addEventListener("click", () => {
      const item = store.selected();
      if (item?.kind !== "house") return;
      item.wall = b.getAttribute("data-wall") as WallKind;
      rebuild();
    });
  });
  document.querySelectorAll("[data-place]").forEach((b) => {
    b.addEventListener("click", () => {
      const mode = b.getAttribute("data-place") as "door" | "window";
      store.setPlace(mode);
      store.notify();
    });
  });
  document.querySelectorAll("[data-field]").forEach((b) => {
    b.addEventListener("click", () => {
      const item = store.selected();
      if (item?.kind !== "field") return;
      applyFieldKind(item, b.getAttribute("data-field") as FieldKind);
      rebuild();
    });
  });
  wallLen.addEventListener("change", () => {
    const item = store.selected();
    if (item?.kind !== "wall") return;
    item.width = num(wallLen.value, 0.4);
    clampDoor(item);
    rebuild();
  });
  wallTh.addEventListener("change", () => {
    const item = store.selected();
    if (item?.kind !== "wall") return;
    item.depth = num(wallTh.value, 0.08);
    rebuild();
  });
  wallH.addEventListener("change", () => {
    const item = store.selected();
    if (item?.kind !== "wall") return;
    item.height = num(wallH.value, 0.8);
    rebuild();
  });
  const editAperture = (fn: (ap: ReturnType<typeof wallApertures>[number]) => void) => {
    const item = store.selected();
    if (item?.kind !== "wall") return;
    const aps = ensureApertures(item);
    const ap = aps.find((a) => a.id === store.apertureId) ?? aps[0];
    if (!ap) return;
    store.apertureId = ap.id;
    fn(ap);
    clampAperture(item, ap);
    rebuild();
  };
  doorW.addEventListener("change", () => {
    editAperture((ap) => {
      ap.width = num(doorW.value, 0.4);
    });
  });
  doorH.addEventListener("change", () => {
    editAperture((ap) => {
      ap.height = num(doorH.value, 0.4);
    });
  });
  doorOff.addEventListener("change", () => {
    editAperture((ap) => {
      ap.offset = num(doorOff.value, 0.05);
    });
  });
  doorSill.addEventListener("change", () => {
    editAperture((ap) => {
      ap.sill = num(doorSill.value, 0);
    });
  });
  $("btn-del-aperture").addEventListener("click", () => {
    const item = store.selected();
    if (item?.kind !== "wall") return;
    if (!store.apertureId) store.apertureId = ensureApertures(item)[0]?.id ?? null;
    store.removeSelectedOpening();
    rebuild();
  });
  const pickOpening = (ev: Event) => {
    const btn = (ev.target as HTMLElement).closest("[data-opening-id]");
    if (!(btn instanceof HTMLElement)) return;
    store.apertureId = btn.dataset.openingId ?? null;
    editor.updateSelection();
    store.notify();
  };
  houseOpeningList.addEventListener("click", pickOpening);
  roomOpeningList.addEventListener("click", pickOpening);
  const delOpening = () => {
    const item = store.selected();
    if (item?.kind !== "house" && item?.kind !== "room") return;
    if (!store.apertureId) store.apertureId = item.openings?.[0]?.id ?? null;
    store.removeSelectedOpening();
    rebuild();
  };
  btnDelHouseOpening.addEventListener("click", delOpening);
  btnDelRoomOpening.addEventListener("click", delOpening);
  document.querySelectorAll("[data-cover]").forEach((b) => {
    b.addEventListener("click", () => {
      const item = store.selected();
      if (item?.kind !== "canopy") return;
      item.cover = b.getAttribute("data-cover") === "losa" ? "losa" : "chapa";
      rebuild();
    });
  });
  canopyH0.addEventListener("change", () => {
    const item = store.selected();
    if (item?.kind !== "canopy") return;
    item.height = num(canopyH0.value, 0.5);
    rebuild();
  });
  canopyH1.addEventListener("change", () => {
    const item = store.selected();
    if (item?.kind !== "canopy") return;
    item.heightEnd = num(canopyH1.value, 0.5);
    rebuild();
  });
  document.querySelectorAll("[data-room-name]").forEach((b) => {
    b.addEventListener("click", () => {
      const item = store.selected();
      if (item?.kind !== "room") return;
      item.name = b.getAttribute("data-room-name") ?? item.name;
      rebuild();
    });
  });
  document.querySelectorAll("[data-room-face]").forEach((b) => {
    b.addEventListener("click", () => {
      const item = store.selected();
      if (item?.kind !== "room") return;
      store.wallFace = Number(b.getAttribute("data-room-face")) as WallFace;
      editor.updateSelection();
      store.notify();
    });
  });
  $("btn-room-equal").addEventListener("click", () => {
    const item = store.selected();
    if (item?.kind !== "room") return;
    setAllRoomWallHeights(item, item.height);
    store.wallFace = null;
    rebuild();
  });

  $("btn-new").addEventListener("click", async () => {
    if (!store.uid) return;
    if (!confirm("¿Crear un proyecto nuevo? El actual ya quedó guardado en tu cuenta.")) return;
    await store.createCloud("Mi rancho", defaultProject());
    editor.rebuild();
    store.notify();
  });
  projectSelect.addEventListener("change", async () => {
    if (!projectSelect.value) return;
    await store.openCloud(projectSelect.value);
    editor.rebuild();
    store.notify();
  });
  projectTitle.addEventListener("change", () => {
    store.renameTitle(projectTitle.value);
  });
  $("btn-delete-project").addEventListener("click", async () => {
    if (!store.cloudId) return;
    if (!confirm(`¿Borrar “${store.title}” de tu cuenta? No se puede deshacer.`)) return;
    await store.deleteCloud();
    editor.rebuild();
    store.notify();
  });
  $("btn-save").addEventListener("click", () => {
    const payload = buildProjectFile(store.title, store.project);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileNameFromTitle(store.title);
    a.click();
    URL.revokeObjectURL(a.href);
  });
  const shareBox = $("share-box");
  const shareUrl = $<HTMLInputElement>("share-url");
  const shareMsg = $("share-msg");
  const shareHours = $<HTMLSelectElement>("share-hours");
  $("btn-exit-view").addEventListener("click", () => {
    location.assign(location.pathname);
  });
  $("btn-share").addEventListener("click", () => {
    shareBox.hidden = !shareBox.hidden;
    shareMsg.hidden = true;
  });
  $("btn-share-copy").addEventListener("click", async () => {
    if (!store.uid) return;
    const hours = Number(shareHours.value) as ShareHours;
    shareMsg.hidden = false;
    shareMsg.textContent = "Creando enlace…";
    try {
      const { url, expiresAt } = await createShareLink(store.uid, store.title, store.project, hours);
      shareUrl.hidden = false;
      shareUrl.value = url;
      shareUrl.select();
      try {
        await navigator.clipboard.writeText(url);
        shareMsg.textContent = `Copiado. Vence ${new Date(expiresAt).toLocaleString("es-AR", {
          dateStyle: "short",
          timeStyle: "short",
        })}.`;
      } catch {
        shareMsg.textContent = "Seleccioná el enlace y copialo (Ctrl+C).";
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      shareMsg.textContent = /permission/i.test(msg)
        ? "Falta publicar las reglas de Firestore (colección shares) en Firebase."
        : "No pude crear el enlace. Probá de nuevo.";
    }
  });
  $("btn-open").addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    fileInput.value = "";
    if (!file) return;
    try {
      const parsed = parseProjectFile(JSON.parse(await file.text()));
      store.cloudId = null;
      store.replace(parsed.data);
      store.renameTitle(parsed.title);
      editor.rebuild();
      if (store.uid) {
        try {
          await store.createCloud(parsed.title, parsed.data);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "";
          store.cloudError = /permission/i.test(msg)
            ? "El archivo se abrió. Falta publicar las reglas de Firestore en Firebase (Authentication ya funciona)."
            : "El archivo se abrió, pero no se pudo guardar en la nube.";
          store.notify();
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "formato";
      if (/permission/i.test(msg)) {
        store.cloudError = "El archivo se abrió. Falta publicar las reglas de Firestore en Firebase.";
        store.notify();
        return;
      }
      alert(`No pude abrir ese archivo (${msg}). Probá de nuevo con Exportar archivo desde Mi Rancho.`);
    }
  });
}

function hintText(): string {
  if (store.viewOnly) {
    return "Orbitá con el mouse · rueda: zoom · Arrastrá la N para el norte · F enfoca · C centro";
  }
  const mode = store.placeMode;
  if (mode === "wall") {
    if (store.selectedIds.length === 2 && !store.wallStart) {
      return "Clickeá cerca del primer seleccionado: el extremo se pega ahí";
    }
    if (store.selectedIds.length === 2 && store.wallStart) {
      return "Clickeá cerca del segundo seleccionado: el otro extremo se pega ahí";
    }
    return store.wallStart
      ? "Clickeá el otro extremo: se pega a una pared o ambiente cercano"
      : "Clickeá el primer extremo: se pega a una pared o ambiente cercano";
  }
  if (isAddKind(mode)) {
    const entry = CATALOG.find((c) => c.kind === mode);
    return entry?.hint ?? "Clickeá el terreno";
  }
  if (mode === "door") return "Clickeá la pared de una casa, ambiente o tabique para poner la puerta";
  if (mode === "window") return "Clickeá la pared de una casa, ambiente o tabique para poner la ventana";
  if (store.apertureId) return "Abertura seleccionada · Quitar abertura o Supr la elimina · Esc cancela";
  if (store.selected()?.kind === "room") {
    return store.wallFace !== null
      ? `Pared ${WALL_FACE_LABEL[store.wallFace]}: cambiá Alto para esa pared. Igualar las 4 las deja iguales`
      : "Clickeá una pared del ambiente para cambiar solo su alto (techo con caída)";
  }
  return "Arrastrá la N para el norte · Ctrl+Z deshace · Doble click: eje de giro · F enfoca · C centro · R rota · Supr elimina";
}

function fillOpeningList(box: HTMLElement, item: Item | null): void {
  box.innerHTML = "";
  if (!item?.openings?.length) return;
  for (const op of item.openings) {
    const b = document.createElement("button");
    b.type = "button";
    b.dataset.openingId = op.id;
    b.textContent = `${op.kind === "door" ? "Puerta" : "Ventana"} ${WALL_FACE_LABEL[op.wall]}`;
    b.classList.toggle("active", store.apertureId === op.id);
    box.appendChild(b);
  }
}

function num(v: string, min: number): number {
  const n = Number.parseFloat(v.replace(",", "."));
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.round(n * 10) / 10);
}

function wrapDeg(v: string): number {
  let n = Number.parseFloat(v.replace(",", "."));
  if (!Number.isFinite(n)) return 0;
  n %= 360;
  if (n < 0) n += 360;
  if (n >= 360) n = 0;
  return Math.round(n * 10) / 10;
}

function degFromRad(rad: number): number {
  return wrapDeg(String((rad * 180) / Math.PI));
}

function formatHour(h: number): string {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
